import {
  CognitiveCompletionRequest,
  CognitivePrompt,
} from '../../tasks/api/index';

export interface ContactAIPromptConfig {
  system: string;
  userTemplate: string;
}

export const CONTACT_EXTRACTION_PROMPTS: ContactAIPromptConfig = {
  system: `Du bist ein intelligenter Assistent für die Extraktion von Kontaktinformationen aus E-Mails. Deine Aufgabe ist es, aus E-Mail-Daten (Absender, Betreff, Inhalt/Signatur) strukturierte Kontaktinformationen zu extrahieren.

REGELN:
1. Extrahiere NUR die Kontaktdaten des ABSENDERS (siehe ABSENDER-Feld). Ignoriere Kontaktdaten anderer Personen, die im E-Mail-Verlauf, in CC, in weitergeleiteten Nachrichten oder im Text erwähnt werden.
2. Die wichtigste Datenquelle ist die E-MAIL-SIGNATUR des Absenders. Bei Antwort-Ketten steht die Signatur des Absenders VOR dem zitierten Verlauf (erkennbar an Trennzeilen wie "Von:", "From:", "-----Ursprüngliche Nachricht-----", "On ... wrote:" etc.). Erkenne die Signatur anhand typischer Muster (Name, Position, Firma, Telefonnummern, Adresse untereinander).
3. Wenn die E-Mail mehrere Signaturen enthält (z.B. bei Antwort-Ketten), verwende NUR die ERSTE Signatur, die zum ABSENDER (ABSENDER-Feld) passt. Signaturen nach Trennzeilen wie "Von:", "From:" gehören zu früheren Nachrichten anderer Personen und MÜSSEN ignoriert werden.
4. Gib NUR Felder zurück, die du tatsächlich in der Signatur oder im Absender-Header findest.
5. Lasse Felder weg, die nicht eindeutig erkennbar sind (NICHT null setzen).
6. Bei Telefonnummern: Behalte das Originalformat bei.
7. Bei Geschlecht: Leite es aus dem Namen oder der Anrede ab (z.B. "Sehr geehrter Herr" = "male", "Sehr geehrte Frau" = "female").

WICHTIG: Gib NUR reines JSON zurück, KEINE Markdown-Formatierung, KEINE Code-Blöcke mit \`\`\`json, nur das reine JSON-Objekt!

OUTPUT FORMAT (strikt JSON ohne Markdown, nur gefundene Felder):
{
  "gender": "male | female | diverse",
  "firstName": "string",
  "lastName": "string",
  "position": "string",
  "department": "string",
  "phone": "string",
  "cellphone": "string",
  "fax": "string",
  "companyName": "string",
  "companyStreet": "string",
  "companyZip": "string",
  "companyCity": "string",
  "companyPhone": "string",
  "companyWebsite": "string"
}`,
  userTemplate: `Extrahiere Kontaktinformationen aus folgender E-Mail:

ABSENDER:
{{EMAIL_FROM}}

BETREFF:
{{EMAIL_SUBJECT}}

E-MAIL INHALT:
{{EMAIL_BODY}}

Extrahiere die Kontaktdaten des ABSENDERS. Fokussiere dich auf die Signatur des Absenders — diese enthält typischerweise Name, Position, Firma, Telefonnummern und Adresse. Ignoriere Kontaktdaten anderer Personen im E-Mail-Verlauf.

Gib NUR reines JSON zurück ohne \`\`\`json Markdown-Formatierung!`,
};

/**
 * Strips HTML tags from a string and returns plain text.
 */
function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Builds the CognitiveCompletionRequest for contact extraction.
 * Handles HTML stripping and body truncation internally.
 */
export function buildContactExtractionPrompt(
  emailFrom: string,
  emailSubject: string,
  emailBody: string,
  bodyContentType?: string
): CognitiveCompletionRequest {
  // Strip HTML unless explicitly plain text
  let plainBody = bodyContentType !== 'text' ? stripHtml(emailBody) : emailBody;

  // Truncate to first 3000 characters (sender's signature comes before quoted reply chains)
  if (plainBody.length > 3000) {
    plainBody = plainBody.slice(0, 3000);
  }

  const userMessage = CONTACT_EXTRACTION_PROMPTS.userTemplate
    .replace('{{EMAIL_FROM}}', emailFrom)
    .replace('{{EMAIL_SUBJECT}}', emailSubject)
    .replace('{{EMAIL_BODY}}', plainBody);

  return {
    prompts: [
      {
        message: CONTACT_EXTRACTION_PROMPTS.system,
        promptType: 'system',
      } as CognitivePrompt,
      {
        message: userMessage,
        promptType: 'user',
      } as CognitivePrompt,
    ],
  };
}
